/**
 * Area and Line — `docs/property-maps-v2.md` §5.3, §9.3.
 *
 * One class, because they are the same gesture with two endings: click the
 * corners, the last one follows the pointer, and finishing closes the ring or
 * does not. Keeping them apart would be two copies of the Backspace, Escape
 * and ⇧ handling, which is three chances for them to drift.
 *
 * **A click adds a point; a double-click finishes.** The hint chip says so
 * (§5.3), and `Enter` does the same for anybody working from the keyboard.
 * `Backspace` removes the last point and `Esc` cancels the whole shape —
 * never the tool, which is the editor's decision.
 *
 * **⇧ constrains a line to 45°** from the previous point. On an area it does
 * the same, because an area's edges are lines and somebody holding ⇧ means
 * the same thing either way.
 */
import type { Geometry, LngLat } from '@rowted/shared'
import type { Draft, DrawEvent, DrawTool, KeyEvent, ScreenPoint, ToolOptions } from './types.js'

/** Two clicks closer than this are the same click, not a new corner. */
const SAME_POINT_PX = 3

export class PathTool implements DrawTool {
  readonly kind: 'area' | 'line'

  readonly hint: string

  readonly autoFinish = false


  private readonly frame: ToolOptions['frame']

  private readonly snap: NonNullable<ToolOptions['snap']>

  /**
   * The corners placed so far, **on the ground**.
   *
   * They were kept in screen space, which is true only while the map holds
   * still — and under the crosshair (§6.2) the map is what moves: one finger
   * pans the property under a fixed sight, so a corner stored at a screen
   * pixel would slide across the site with every pan and the shape would
   * come out somewhere nobody drew it. A corner is a place, so it is stored
   * as one and projected back whenever the screen needs it.
   */
  private points: LngLat[] = []

  /** Where the pointer is, drawn as the edge that has not been placed yet. */
  private hover: ScreenPoint | null = null

  constructor(kind: 'area' | 'line', options: ToolOptions) {
    this.kind = kind
    this.frame = options.frame
    this.snap = options.snap ?? ((point) => point)
    this.hint = kind === 'area'
      ? 'Click corners · double-click to finish'
      : 'Click along the line · double-click to finish'
  }

  onPointerDown(event: DrawEvent): void {
    const point = this.constrain(this.snap(event.point, event), event)
    const last = this.lastPoint()
    /*
     * A double-click arrives as a second `pointerdown` in the same place, so
     * the tool finishes rather than placing a corner on top of one. Without
     * this the ring gains a duplicate point and `geometryProblem` counts it
     * as two, which is how a triangle drawn with a double-click to finish
     * used to come out with three distinct corners and one invisible fourth.
     */
    /*
     * The comparison is in screen space **now**, which is what makes it mean
     * the right thing in both places: on a desktop a double-click's two
     * clicks are the same pixel and the same ground; under the crosshair
     * every tap is the same pixel and different ground, so the previous
     * corner has moved away from the sight and the test passes.
     */
    if (last && near(last, point)) {
      this.hover = null
      return
    }
    this.points.push(this.frame.toLngLat(point))
  }

  /** The last corner, where it is on the screen right now. */
  private lastPoint(): ScreenPoint | null {
    const last = this.points[this.points.length - 1]
    return last ? this.frame.toPoint(last) : null
  }

  onPointerMove(event: DrawEvent): void {
    if (this.points.length === 0) return
    this.hover = this.constrain(this.snap(event.point, event), event)
  }

  onPointerUp(): void { /* A path is clicks, not a drag. */ }

  onKey(event: KeyEvent): void {
    if (event.key === 'Backspace') {
      this.points.pop()
      if (this.points.length === 0) this.hover = null
      return
    }
    if (event.key === 'Escape') this.cancel()
  }

  cancel(): void {
    this.points = []
    this.hover = null
  }

  draft(): Draft | null {
    const placed = this.points
    if (placed.length === 0) return null
    /* The hovered point is drawn but is not a corner until it is clicked. */
    const all = this.hover ? [...placed, this.frame.toLngLat(this.hover)] : placed
    return this.build(all, placed.length)
  }

  finish(): Draft | null {
    const made = this.build(this.points, this.points.length)
    if (!made?.complete) return null
    this.cancel()
    return { ...made, complete: true }
  }

  private build(world: LngLat[], placed: number): Draft | null {
    if (world.length === 0) return null

    if (this.kind === 'line') {
      const geometry: Geometry = { type: 'LineString', coordinates: world }
      return { kind: 'line', geometry, complete: placed >= 2 }
    }

    /*
     * A ring repeats its first point to close. With fewer than three corners
     * there is no inside yet, so the draft is drawn as the open path it is —
     * MapLibre renders a `LineString` happily and a one-sided "polygon" would
     * paint a fill nobody asked for.
     */
    if (world.length < 3) {
      return { kind: 'area', geometry: { type: 'LineString', coordinates: world }, complete: false }
    }
    const ring: LngLat[] = [...world, world[0]!]
    return { kind: 'area', geometry: { type: 'Polygon', coordinates: [ring] }, complete: placed >= 3 }
  }

  /** `⇧` snaps the next edge to the nearest 45° from the previous corner. */
  private constrain(point: ScreenPoint, event: DrawEvent): ScreenPoint {
    const last = this.lastPoint()
    if (!event.shift || !last) return point

    const dx = point.x - last.x
    const dy = point.y - last.y
    const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4)
    const length = Math.hypot(dx, dy)
    return {
      x: last.x + Math.cos(angle) * length,
      y: last.y + Math.sin(angle) * length,
    }
  }
}

const near = (a: ScreenPoint, b: ScreenPoint): boolean =>
  Math.hypot(a.x - b.x, a.y - b.y) <= SAME_POINT_PX
